import {useUrl} from '@shopify/hydrogen';
import {
  useState,
  useMemo,
  useCallback,
  useContext,
  createContext,
  useEffect,
} from 'react';
import SwalComponent from '../components/modals/SwalComponent.client';
import {ModalNameType} from '../constants';

type ModalUIContextStateType = {
  readonly openModalName: string;
};

type ModalUIContextActionType = {
  openModalUI: (modalName: ModalNameType) => void;
  closeModalUI: (modalName?: ModalNameType) => void;
  toggleModalUI: (modalName: ModalNameType) => void;
};

type ModalUIContextType = ModalUIContextStateType & ModalUIContextActionType;

const ModalUIContext = createContext<ModalUIContextType>(null);

function ModalUIProvider({children}) {
  const [openModalName, setOpenModalName] = useState<string>(null);

  const openModalUI = useCallback((modalName) => {
    setOpenModalName(modalName);
  }, []);

  const closeModalUI = useCallback(
    (modalName?: string) =>
      setOpenModalName((curModal) =>
        !modalName || curModal === modalName ? null : curModal,
      ),
    [],
  );

  const toggleModalUI = useCallback(
    (modalName: string) =>
      setOpenModalName((curModal) =>
        curModal === modalName ? null : modalName,
      ),
    [],
  );

  const contextValue = useMemo(() => {
    return {
      openModalName,
      openModalUI,
      closeModalUI,
      toggleModalUI,
    };
  }, [closeModalUI, openModalName, openModalUI, toggleModalUI]);

  // close modals when navigating to a new path
  const {pathname} = useUrl();
  useEffect(() => {
    closeModalUI();
    SwalComponent.close();
  }, [closeModalUI, pathname]);

  return (
    <ModalUIContext.Provider value={contextValue}>
      {children}
    </ModalUIContext.Provider>
  );
}

function useModalUI() {
  const modalUI = useContext(ModalUIContext);

  if (modalUI === undefined) {
    throw new Error('ModalUI context is undefined');
  }

  return modalUI;
}

export {ModalUIProvider, useModalUI};
